import { useState, useEffect } from 'react';
import {
  onSnapshot,
  doc,
  collection,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Game, User, AppConfig } from '@/lib/types';
import * as SystemService from '@/services/system.service';

const DEFAULT_APP_CONFIG = {
  id: 'app',
  default_buy_in: 20,
  telegram_group_id: '',
  tournament_alert_threshold: 0,
  proximity_radius_meters: 0,
  expense_categories: [],
  drive_folder_id: '',
  service_account_email: '',
  app_version: '0.1.001',
  default_game_title: 'Poker Night',
  default_game_day: 5,
  default_game_time: '20:00',
  location_list: [{ id: 'default-venue', label: 'Poker Room', address: '', notes: null }],
  default_location_id: 'default-venue',
  successor_user_id: null,
  currency_symbol: '$',
  amount_increment: 0.5,
  min_players: 2,
  telegram_chat_configs: [],
  telegram_active_chat_id: '',
} satisfies AppConfig & { id: string };

const SNAPSHOT_FALLBACK_MS = 3000;

interface UseGamesReturn {
  games: Game[];
  loading: boolean;
}

export function useGames(limit?: number): UseGamesReturn {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setGames([]);
      setLoading(false);
    }, SNAPSHOT_FALLBACK_MS);

    let q = query(
      collection(db, 'games'),
      where('is_deleted', '==', false),
      orderBy('date', 'desc')
    );
    if (limit) q = query(q, where('__name__', '!=', '__placeholder__'));

    const unsub = onSnapshot(
      q,
      (snap) => {
        settled = true;
        window.clearTimeout(timeout);
        const gameList = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as Game))
          .filter(g => !g.is_deleted)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        SystemService.archiveStaleGames(gameList).catch(console.error);

        setGames(limit ? gameList.slice(0, limit) : gameList);
        setLoading(false);
      },
      (error) => {
        settled = true;
        window.clearTimeout(timeout);
        console.error('useGames error:', error);
        setGames([]);
        setLoading(false);
      }
    );
    return () => {
      settled = true;
      window.clearTimeout(timeout);
      unsub();
    };
  }, [limit]);

  return { games, loading };
}

interface UseUsersReturn {
  users: User[];
  loading: boolean;
}

export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setUsers([]);
      setLoading(false);
    }, SNAPSHOT_FALLBACK_MS);

    const unsub = onSnapshot(
      query(collection(db, 'users'), where('is_active', '==', true)),
      (snap) => {
        settled = true;
        window.clearTimeout(timeout);
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() }) as User));
        setLoading(false);
      },
      (error) => {
        settled = true;
        window.clearTimeout(timeout);
        console.error('useUsers error:', error);
        setLoading(false);
      }
    );
    return () => {
      settled = true;
      window.clearTimeout(timeout);
      unsub();
    };
  }, []);

  return { users, loading };
}

export function useAdminUsers(): UseUsersReturn {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setUsers([]);
      setLoading(false);
    }, SNAPSHOT_FALLBACK_MS);

    const unsub = onSnapshot(
      collection(db, 'users'),
      (snap) => {
        settled = true;
        window.clearTimeout(timeout);
        const adminUsers = snap.docs
          .map(d => ({ id: d.id, ...d.data() }) as User)
          .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
        setUsers(adminUsers);
        setLoading(false);
      },
      (error) => {
        settled = true;
        window.clearTimeout(timeout);
        console.error('useAdminUsers error:', error);
        setUsers([]);
        setLoading(false);
      }
    );
    return () => {
      settled = true;
      window.clearTimeout(timeout);
      unsub();
    };
  }, []);

  return { users, loading };
}

interface UseAppConfigReturn {
  config: AppConfig | null;
  loading: boolean;
}

export function useAppConfig(): UseAppConfigReturn {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setConfig(DEFAULT_APP_CONFIG);
      setLoading(false);
    }, SNAPSHOT_FALLBACK_MS);

    const unsub = onSnapshot(
      doc(db, 'config', 'app'),
      (snap) => {
        settled = true;
        window.clearTimeout(timeout);
        setConfig(snap.exists() ? ({ id: snap.id, ...snap.data() } as unknown as AppConfig) : DEFAULT_APP_CONFIG);
        setLoading(false);
      },
      (error) => {
        settled = true;
        window.clearTimeout(timeout);
        console.error('useAppConfig error:', error);
        setConfig(DEFAULT_APP_CONFIG);
        setLoading(false);
      }
    );
    return () => {
      settled = true;
      window.clearTimeout(timeout);
      unsub();
    };
  }, []);

  return { config, loading };
}

interface UsePreGameReturn {
  preGame: Game | null;
  loading: boolean;
}

export function usePreGame(): UsePreGameReturn {
  const [preGame, setPreGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setPreGame(null);
      setLoading(false);
    }, SNAPSHOT_FALLBACK_MS);

    try {
      const q = query(
        collection(db, 'games'),
         where('status', '==', 'setup'),
         where('is_deleted', '==', false),
         orderBy('date', 'desc')
       );
      const unsub = onSnapshot(q, (snap) => {
        settled = true;
        window.clearTimeout(timeout);
        if (!snap.empty) {
          const doc = snap.docs[0];
          setPreGame({ id: doc.id, ...doc.data() } as Game);
        } else {
          setPreGame(null);
        }
        setLoading(false);
      }, (error) => {
        settled = true;
        window.clearTimeout(timeout);
        console.error('usePreGame error:', error);
        setLoading(false);
      });
      return () => {
        settled = true;
        window.clearTimeout(timeout);
        unsub();
      };
    } catch (err) {
      settled = true;
      window.clearTimeout(timeout);
      console.error('usePreGame setup error:', err);
      setLoading(false);
    }
  }, []);

  return { preGame, loading };
}

interface UseActiveGameReturn {
  activeGame: Game | null;
  loading: boolean;
}

export function useActiveGame(): UseActiveGameReturn {
  const [activeGame, setActiveGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      setActiveGame(null);
      setLoading(false);
    }, SNAPSHOT_FALLBACK_MS);

    try {
      const q = query(
        collection(db, 'games'),
        where('status', 'in', ['active', 'chip_entry', 'settling', 'settled']),
        where('is_deleted', '==', false),
        orderBy('date', 'desc')
      );
       const unsub = onSnapshot(q, (snap) => {
         settled = true;
         window.clearTimeout(timeout);
         if (!snap.empty) {
            const games = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Game));

            const activeOrChip = games.find(g => g.status === 'active' || g.status === 'chip_entry');
            const settling = games.find(g => g.status === 'settling');
            const settled = games.find(g => g.status === 'settled');
            setActiveGame(activeOrChip || settling || settled || null);
         } else {
           setActiveGame(null);
         }
         setLoading(false);
       }, (error) => {
        settled = true;
        window.clearTimeout(timeout);
        console.error('useActiveGame error:', error);
        setLoading(false);
      });
      return () => {
        settled = true;
        window.clearTimeout(timeout);
        unsub();
      };
    } catch (err) {
      settled = true;
      window.clearTimeout(timeout);
      console.error('useActiveGame setup error:', err);
      setLoading(false);
    }
  }, []);

  return { activeGame, loading };
}

export async function dismissGame(gameId: string): Promise<void> {
  return SystemService.dismissGame(gameId);
}
